import { useState } from 'react';
import { post } from '../lib/api';
import { ErrorNote, Sheet } from './ui';
import { formatMinutes, money } from '../lib/format';
import type { Claim, Extra } from '../lib/types';

/** "I did this": a mandatory note, and quarter-hours for hourly extras. */
export default function ClaimSheet({
  extra,
  currency,
  childId,
  onClose,
  onClaimed,
}: {
  extra: Extra;
  currency: string;
  childId?: number; // set when a parent logs it for a child
  onClose: () => void;
  onClaimed: (claim: Claim) => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const worth = extra.pricing === 'hourly' ? Math.round(extra.amount * (minutes / 60) * 100) / 100 : extra.amount;

  async function submit() {
    setError(null);
    if (note.trim().length < 3) {
      setError('Say what you did, in a few words.');
      return;
    }
    setBusy(true);
    try {
      const res = await post<{ claim: Claim }>(`/api/extras/${extra.id}/claims`, {
        note: note.trim(),
        ...(extra.pricing === 'hourly' ? { minutes } : {}),
        ...(childId ? { childId } : {}),
      });
      onClaimed(res.claim);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={`${extra.icon} ${extra.title}`} onClose={onClose}>
      <ErrorNote error={error} />
      {extra.notes && <p className="small muted">{extra.notes}</p>}

      {extra.pricing === 'hourly' && (
        <>
          <div className="tiny muted center">How long did it take?</div>
          <div className="q-stepper">
            <button type="button" onClick={() => setMinutes((m) => Math.max(15, m - 15))} aria-label="Less">−</button>
            <span>{formatMinutes(minutes)}</span>
            <button type="button" onClick={() => setMinutes((m) => Math.min(12 * 60, m + 15))} aria-label="More">+</button>
          </div>
        </>
      )}
      <div className="q-worth">
        {extra.pricing === 'hourly'
          ? `${money(extra.amount, currency)} an hour = ${money(worth, currency)}`
          : `Worth ${money(worth, currency)}`}
      </div>

      <label className="field">
        <span>What did you do?</span>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={extra.pricing === 'hourly' ? 'Dug out the weeds by the fence' : 'Read "Emil und die Detektive"'}
          maxLength={300}
        />
      </label>

      <button className="btn block" type="button" onClick={submit} disabled={busy}>
        {busy ? 'Sending…' : extra.needs_approval === 1 && !childId ? 'Send to Mum & Dad' : 'Add it'}
      </button>
    </Sheet>
  );
}
